/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAuthSession } from "../Helpers/common";
import {
  hangupInboundCall,
  hangupOutBoundCall,
  hangupOutBoundMonitorCall,
} from "../Helpers/CallCommon";
import {
  setInBoundCallPopUpShow,
  setInBoundCallPopUpVisibility,
  setMinimizePopUpShow,
  setOutBoundCallPopUpShow,
  setOutBoundCallPopUpVisibility,
} from "../redux/callSlice";
import { setLocalJid } from "../Helpers/Lib/libSlice";
import Draggable from "react-draggable";

const MinimizePopUp = () => {
  const [callerName, setCallerName] = useState("");
  const { user_unique_key } = getAuthSession();

  const { call, chat } = useSelector((state) => state || {});
  const { allAttendeeList, minimizePopUpInnerData, callDetail } = call || {};
  const { groupList, minimizePopUpShow } = chat || {};

  const { callType } = minimizePopUpInnerData;
  const dispatch = useDispatch();

  const [dragPosition, setDragPosition] = useState(null);
  const hasUserDraggedRef = useRef(false);
  const prevMinimizePopUpShowRef = useRef(minimizePopUpShow);

  useEffect(() => {
    const { callerName: nextCallerName } = minimizePopUpInnerData;
    setCallerName(nextCallerName);
  }, [minimizePopUpInnerData]);

  const ref = useRef(null);

  const [pipSize, setPipSize] = useState({ width: 0, height: 0 });
  const [viewport, setViewport] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const update = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
      if (ref.current) {
        setPipSize({
          width: ref.current.offsetWidth,
          height: ref.current.offsetHeight,
        });
      }
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const dragBounds = useMemo(() => {
    if (!viewport.width || !viewport.height) {
      return undefined;
    }

    const fallbackWidth = 12 * 16;
    const fallbackHeight = 6.9 * 16;

    const width = pipSize.width || fallbackWidth;
    const height = pipSize.height || fallbackHeight;

    const overflowX = width * 0.75;
    const overflowY = height * 0.75;

    return {
      left: -overflowX,
      top: -overflowY,
      right: viewport.width - width + overflowX,
      bottom: viewport.height - height + overflowY,
    };
  }, [pipSize, viewport]);

  const initialPosition = useMemo(() => {
    if (!viewport.width || !viewport.height) {
      return { x: 0, y: 0 };
    }
    const fallbackWidth = 12 * 16;
    const fallbackHeight = 6.9 * 16;
    const width = pipSize.width || fallbackWidth;
    const height = pipSize.height || fallbackHeight;
    const viewportPadding = Math.max(
      20,
      Math.min(28, Math.min(viewport.width, viewport.height) * 0.05),
    );
    const maxX = Math.max(0, viewport.width - width - viewportPadding);
    const maxY = Math.max(0, viewport.height - height - viewportPadding);
    return { x: maxX, y: maxY };
  }, [pipSize, viewport]);

  useEffect(() => {
    if (hasUserDraggedRef.current) {
      return;
    }
    setDragPosition(initialPosition);
  }, [initialPosition]);

  useEffect(() => {
    const wasShown = prevMinimizePopUpShowRef.current;
    const isShown = minimizePopUpShow;
    prevMinimizePopUpShowRef.current = minimizePopUpShow;

    // When the popup is opened again, reset it to bottom-right.
    if (!wasShown && isShown) {
      hasUserDraggedRef.current = false;
      setDragPosition(initialPosition);
    }
  }, [initialPosition, minimizePopUpShow]);

  const containerStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    zIndex: 9998,
    pointerEvents: "none",
  };

  const windowStyle = {
    position: "absolute",
    pointerEvents: "auto",
  };

  const MainCallPopUpHandler = () => {
    if (callType === "inbound") {
      dispatch(setInBoundCallPopUpVisibility(false));
    } else {
      dispatch(setOutBoundCallPopUpVisibility(false));
    }
  };

  const DeclineCall = async () => {
    const { type, calling_status, participantID } = callDetail;
    if (callType === "inbound") {
      let callingType = "decline";
      if (type === "private") {
        callingType = calling_status === "initiated" ? "decline" : "ended";
      } else {
        let index = participantID.findIndex((v) => v.id === user_unique_key);
        let participant = [...participantID];
        callingType =
          participant[index].status === "initiated" ? "decline" : "ended";
      }
      await hangupInboundCall(callingType);
      dispatch(setInBoundCallPopUpShow(false));
      dispatch(setLocalJid(null));
      dispatch(setInBoundCallPopUpVisibility(false));
    } else {
      if (
        callDetail?.monitor_status === undefined ||
        callDetail?.monitor_status === false
      ) {
        await hangupOutBoundCall(
          callDetail,
          "ended",
          allAttendeeList,
          user_unique_key,
          groupList,
        );
      } else {
        await hangupOutBoundMonitorCall(callDetail, allAttendeeList);
      }
      dispatch(setOutBoundCallPopUpShow(false));
      dispatch(setOutBoundCallPopUpVisibility(false));
    }
    dispatch(setMinimizePopUpShow(false));
  };

  useEffect(() => {
    if (callDetail && Object.keys(callDetail).length > 0) {
      const { calling_status, monitor_status } = callDetail;
      if (
        calling_status &&
        calling_status === "decline" &&
        minimizePopUpShow &&
        monitor_status === false
      ) {
        if (callType === "outbound") {
          dispatch(setOutBoundCallPopUpVisibility(false));
          dispatch(setOutBoundCallPopUpShow(true));
        } else {
          dispatch(setInBoundCallPopUpShow(false));
          dispatch(setLocalJid(null));
          dispatch(setInBoundCallPopUpVisibility(false));
        }
        dispatch(setMinimizePopUpShow(false));
      }

      if (
        calling_status &&
        calling_status === "ended" &&
        minimizePopUpShow &&
        monitor_status === false
      ) {
        dispatch(setMinimizePopUpShow(false));
        if (callType === "outbound") {
          dispatch(setOutBoundCallPopUpShow(false));
          dispatch(setOutBoundCallPopUpVisibility(false));
        } else {
          dispatch(setInBoundCallPopUpShow(false));
          dispatch(setLocalJid(null));
          dispatch(setInBoundCallPopUpVisibility(false));
        }
      }
    }
  }, [callDetail, callType, dispatch, minimizePopUpShow]);

  console.log(initialPosition, dragPosition, "initialPosition");

  return (
    <div style={containerStyle}>
      <Draggable
        axis="both"
        bounds={dragBounds}
        position={dragPosition || initialPosition}
        onStart={() => {
          hasUserDraggedRef.current = true;
        }}
        onDrag={(event, data) => {
          setDragPosition({ x: data.x, y: data.y });
        }}
        nodeRef={ref}
      >
        <div ref={ref} className="call-cut-div" style={{ ...windowStyle }}>
          <span className="caller-name">{callerName}</span>
          <div
            className="call-cut-div-icons bg-red cursor-pointer"
            onClick={() => DeclineCall()}
          >
            <svg height="22" width="22" viewBox="0 0 32 32">
              {" "}
              <path
                fill="#ffffff"
                d="M16 12c-2.125 0-4.188.313-6.125.938v4.125c0 .5-.313 1.063-.75 1.25a13.87 13.87 0 00-3.563 2.438c-.25.25-.563.375-.938.375s-.688-.125-.938-.375L.373 17.438c-.25-.25-.375-.563-.375-.938s.125-.688.375-.938c4.063-3.875 9.563-6.25 15.625-6.25s11.563 2.375 15.625 6.25c.25.25.375.563.375.938s-.125.688-.375.938l-3.313 3.313c-.25.25-.563.375-.938.375s-.688-.125-.938-.375a13.87 13.87 0 00-3.563-2.438c-.438-.188-.75-.625-.75-1.188V13c-1.938-.625-4-1-6.125-1z"
              />
            </svg>
          </div>
          <div
            className="minimize-restore-icons"
            onClick={() => {
              dispatch(setMinimizePopUpShow(false));
              MainCallPopUpHandler();
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="css-i6dzq1"
            >
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
          </div>
        </div>
      </Draggable>
    </div>
  );
};

export default React.memo(MinimizePopUp);
